import React, {useState, useEffect} from 'react';
import {Carousel} from 'react-bootstrap';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {
  setSelectedBu,
  addToGlobalStyles,
  setBusFuncMap,
  setWizard,
  setCustomerCardModalTrigger,
  setStrings,
  addToWizard,
  setPatients,
  setBusFuncionalities,
} from '../../../../../store/actions';
import colors from '../../../../../util/colors';
import * as fonts from '../../../../../util/fonts';
import {strings} from '../../../../../util/strings';
import Icon from '../../../../icon';
import {Icons} from '../../../../../util/icons';
import CustomerCardSelectorModal from './customer-card-selector-modal';
import './styles.scss';
import {fillBusMap, deepStringObjectMerger} from '../../../../../util/utility';
import AppSpinner from '../../../../app-spinner';

interface CustomerCardProps {
  selectedBu: any;
  busMap: any;
  buStyles: any;
  busFuncMap: any;
  busFuncMapDefaultObject: any;
  busStylesMap: any;
  availableBus: any[];
  labels: typeof strings.pt.virtualCard;
  setSelectedBu(value: any): any;
  addToGlobalStyles(value: any): void;
  setBusFuncMap(value: any): void;
  setCustomerCardModalTrigger(value: any): void;
  setPatients(value: any): void;
  busFunctionalities: any;
  busGroupExceptions: any;
  busProductExceptions: any;
}

function CustomerCard({
  selectedBu,
  busMap,
  buStyles,
  busFuncMap,
  busFuncMapDefaultObject,
  busStylesMap,
  availableBus,
  labels: labelsFromRedux,
  setSelectedBu,
  addToGlobalStyles,
  setBusFuncMap,
  setCustomerCardModalTrigger,
  busFunctionalities,
  busGroupExceptions,
  busProductExceptions,
  setPatients,
}: CustomerCardProps) {
  const labels = deepStringObjectMerger(
    strings.pt.virtualCard,
    labelsFromRedux,
  );
  const [stagedBu, setStagedBu] = useState(selectedBu);
  const [items, setItems] = useState(
    busMap && busMap?.[stagedBu?.buId]?.[stagedBu?.famId]
      ? busMap?.[stagedBu?.buId]?.[stagedBu?.famId]
      : [],
  );
  const [activeIndex, setActiveIndex] = useState(
    items.findIndex((item: any) => item.memId === stagedBu.memId),
  );
  const [activeModalIndex, setActiveModalIndex] = useState(
    items.findIndex((item: any) => item.memId === stagedBu.memId),
  );
  const [currentStyles, setCurrentStyles] = useState(buStyles);
  const [showCarSelector, setShowCardSelector] = useState(false);
  const [insuranceOptions, setInsuranceOptions] = useState<any[]>([]);

  const featureName = 'CustomerCard';

  const getPatientValidationForWizard = (selectedIndex: any) => {
    const patients = Object.values(availableBus).filter((value: any) => {
      const isCurrentBu =
        value.buId === items[selectedIndex].buId &&
        value.famId === items[selectedIndex].famId;

      return isCurrentBu;
    });
    return {
      patients,
      ...items[selectedIndex],
    };
  };

  useEffect(() => {
    if (!busMap) {
      fillBusMap(featureName);
    } else {
      setItems(
        busMap && busMap?.[stagedBu?.buId]?.[stagedBu?.famId]
          ? busMap?.[stagedBu?.buId]?.[stagedBu?.famId]
          : [],
      );
    }
  }, [busMap]);

  const handleSelect = (selectedIndex: any) => {
    localStorage.setItem('BuId', items[selectedIndex].buId);

    const patientValidation = getPatientValidationForWizard(selectedIndex);

    setPatients({...patientValidation});
    setActiveIndex(selectedIndex);
    setSelectedBu(items[selectedIndex]);
    setStagedBu(items[selectedIndex]);
    setActiveModalIndex(selectedIndex);
  };

  const handleShowCardSelector = () => {
    const selectorOptions = populateInsuranceOptions();
    setInsuranceOptions(selectorOptions);
    setShowCardSelector(true);
    setCustomerCardModalTrigger({showCardSelector: true});
  };

  const populateInsuranceOptions = () => {
    if (busMap) {
      const options = Object.values(busMap).flatMap((buGroups: any) => {
        return Object.values(buGroups).map((group: any) => {
          const groupIsArray = Array.isArray(group);
          let member = groupIsArray
            ? group.find((elem: any) => elem.nif === stagedBu.nif) || group[0]
            : group;

          const optionValue = JSON.stringify(member);

          if (
            stagedBu.card.cardDetail?.cardNbr ===
              member.card.cardDetail?.cardNbr &&
            busFunctionalities
          ) {
            localStorage.setItem('BuId', member.buId);
            setStagedBu(member);
            setSelectedBu(member);
          }

          return {
            label: `${member.buName}${
              member.card && member.card.cardDetail
                ? ` - ${member.card.cardDetail.cardNbr}`
                : ''
            }`,
            value: optionValue,
          };
        });
      });

      const filteredOptions: any[] = options.filter((option) => !!option);

      return filteredOptions;
    }
    return [];
  };

  const setBusFuncMapAngStylesToStore = async (
    newBusFuncMap: any,
    newGlobalStyles: any,
  ) => {
    setBusFuncMap(newBusFuncMap);
    addToGlobalStyles(newGlobalStyles);
    setCurrentStyles(newGlobalStyles);
  };

  const defineActiveFuncs = (exceptionsInactiveFeatures: any[]) => {
    let busFuncMapExceptionRules = JSON.parse(JSON.stringify(busFuncMap));
    Object.keys(busFuncMapExceptionRules).forEach((key: string) => {
      if (key === (stagedBu.buId as string)) {
        const currentBuFuncExceptionRules: any[] =
          busFuncMapExceptionRules[key] || [];

        currentBuFuncExceptionRules.forEach(
          (currentBuFuncExceptionRule: any, index: number) => {
            if (
              exceptionsInactiveFeatures.indexOf(
                currentBuFuncExceptionRule.featureTemplateAlias,
              ) !== -1
            ) {
              busFuncMapExceptionRules[key][index].isVisible = false;
            } else {
              //this store object stores the original state of the isvisible prop for each functionality, before being changed by exceptions
              const funcObjectWithoutException =
                busFuncMapDefaultObject[key][index].isVisible;

              if (funcObjectWithoutException) {
                busFuncMapExceptionRules[key][index].isVisible = true;
              }
            }
          },
        );
      }
    });

    return busFuncMapExceptionRules;
  };

  const getProductsOrGroupsStyleAndInactiveFuncs = async () => {
    const currentBuGroupExceptions =
      busGroupExceptions?.filter(
        (exceptions: any) => exceptions.buAlias === stagedBu.buId,
      ) || [];
    const currentBuProductExceptions =
      busProductExceptions?.filter(
        (exceptions: any) => exceptions.buAlias === stagedBu.buId,
      ) || [];
    const exceptionRules = currentBuGroupExceptions.filter(
      (item: any) => item.groupId === stagedBu.grpId,
    );

    currentBuProductExceptions.forEach((prod: any) => {
      if (prod.productAlias === stagedBu.prdNbr) {
        exceptionRules.push(prod);
      }
    });

    const buProductAndGroupExceptionsInactiveFeatures: string[] =
      [].concat(...exceptionRules.map((rule: any) => rule.inactiveFeatures)) ||
      [];

    if (exceptionRules.length > 0) {
      const busFuncMapExceptionRules = defineActiveFuncs(
        buProductAndGroupExceptionsInactiveFeatures,
      );

      const newGlobalStyles = {
        ...busStylesMap[stagedBu.buId],
        primaryColor: exceptionRules[0].primaryColor,
        secondaryColor: exceptionRules[0].secondaryColor,
        hoverColor: exceptionRules[0].hoverColor,
        url: exceptionRules[0].title,
        logo: exceptionRules[0].emailLogo || busStylesMap[stagedBu.buId].logo,
      };
      setBusFuncMapAngStylesToStore(busFuncMapExceptionRules, newGlobalStyles);
    } else {
      const busFuncMapExceptionRules = defineActiveFuncs(
        buProductAndGroupExceptionsInactiveFeatures,
      );
      const newGlobalStyles = {
        ...busStylesMap[stagedBu.buId],
      };
      setBusFuncMapAngStylesToStore(busFuncMapExceptionRules, newGlobalStyles);
    }
  };

  const renderDirectionIcons = (direction: string) => {
    return (
      items.length > 1 && (
        <div className={`app-header-card-change-container`}>
          <Icon
            name={
              direction === 'prev' ? Icons.CHEVRON_LEFT : Icons.CHEVRON_RIGHT
            }
            color={currentStyles?.primaryColor || colors.blue}
            className={`app-header-card-change-icon`}
          />
        </div>
      )
    );
  };

  const renderDefinedCard = (item: any) => (
    <React.Fragment>
      <img
        src={`data:image/png;charset=utf-8;base64, ${item.card.cardImage}`}
        alt=""
        className="app-header-card-image"
        onClick={handleShowCardSelector}
        style={{cursor: 'pointer'}}
      />
      <div
        onClick={handleShowCardSelector}
        className="app-header-card-view-card-label"
        style={{
          ...fonts.SM,
          ...fonts.SemiBold,
          color: colors.white,
        }}>
        {labels?.VCViewCard}
      </div>
    </React.Fragment>
  );

  const renderUndefinedCard = (item: any) => (
    <div
      className="placeholder-card"
      onClick={handleShowCardSelector}
      style={{
        background: currentStyles?.primaryColor || colors.blue,
        cursor: 'pointer',
      }}>
      <div
        onClick={handleShowCardSelector}
        className="app-header-card-view-card-label"
        style={{
          color: colors.white,
          backgroundColor: currentStyles?.primaryColor || colors.blue,
        }}>
        {labels?.VCViewCard}
      </div>
      <div>
        {(item.card && item.card.cardDetail && item.card.cardDetail.name) ||
          item.memberName}
      </div>
      <div>
        {item.card && item.card.cardDetail && item.card.cardDetail.cardNbr}
      </div>
    </div>
  );

  const renderCarouselItem = (item: any, index: number, card: boolean) => {
    return (
      <Carousel.Item key={index}>
        {card ? renderDefinedCard(item) : renderUndefinedCard(item)}
      </Carousel.Item>
    );
  };

  const renderCarouselItems = () =>
    items.map((item: any, index: number) => {
      const hasCard = !!(item.card && item.card.cardImage);
      return renderCarouselItem(item, index, hasCard);
    });

  const renderCustomizedIndicators = () => {
    return (
      items.length > 1 && (
        <ol className="carousel-indicators">
          {items.map((item: any, index: any) => (
            <li
              key={index}
              onClick={() => {
                handleSelect(index);
              }}
              className={index === activeIndex ? 'active' : ''}
              style={{
                backgroundColor:
                  index === activeIndex && currentStyles.primaryColor,
              }}
            />
          ))}
        </ol>
      )
    );
  };

  useEffect(() => {
    if (busGroupExceptions || busProductExceptions) {
      getProductsOrGroupsStyleAndInactiveFuncs();
    }
  }, [busGroupExceptions, busProductExceptions]);

  useEffect(() => {
    if (items.length > 0) {
      const newItemIndex = items.findIndex(
        (item: any) => item.memId === stagedBu.memId,
      );
      handleSelect(newItemIndex);
      getProductsOrGroupsStyleAndInactiveFuncs();
    }
  }, [items]);

  useEffect(() => {
    if (busMap) {
      const newInsuranceOptions = populateInsuranceOptions();
      setInsuranceOptions(newInsuranceOptions);
    }
  }, [stagedBu.buId, busMap]);

  return !!busMap ? (
    <>
      <Carousel
        activeIndex={activeIndex}
        onSelect={(e) => {
          handleSelect(e);
        }}
        interval={null}
        className="app-header-card"
        indicators={false}
        prevIcon={renderDirectionIcons('prev')}
        nextIcon={renderDirectionIcons('next')}>
        {renderCarouselItems()}
      </Carousel>
      {renderCustomizedIndicators()}
      <CustomerCardSelectorModal
        show={showCarSelector}
        setShow={setShowCardSelector}
        items={items}
        currentStyles={currentStyles}
        activeIndex={activeIndex}
        handleSelect={(e) => {
          handleSelect(e);
        }}
        stagedBu={stagedBu}
        insuranceOptions={insuranceOptions}
        setStagedBu={setStagedBu}
        setItems={setItems}
        activeModalIndex={activeModalIndex}
        setActiveModalIndex={setActiveModalIndex}
      />
    </>
  ) : (
    <span style={{width: '60%'}}>
      <AppSpinner
        type="transparent"
        style={{margin: 'auto', marginTop: '10%'}}></AppSpinner>
    </span>
  );
}
const mapStateToProps = ({
  selectedBu,
  busMap,
  strings,
  busFuncMap,
  busFuncMapDefaultObject,
  busFuncMapDefault,
  wizard,
  globalStyles,
  busStylesMap,
  availableBus,
  busFunctionalities,
  busGroupExceptions,
  busProductExceptions,
  language,
  user,
  tenant,
}: any) => ({
  selectedBu: selectedBu.value,
  busMap: busMap.value,
  reduxStrings: strings.value,
  labels: strings.value.pt.virtualCard ?? {},
  buStyles: globalStyles.value,
  busFuncMap: busFuncMap.value,
  busFuncMapDefaultObject: busFuncMapDefaultObject.value,
  busFuncMapDefault: busFuncMapDefault.value,
  wizard: wizard.value,
  busStylesMap: busStylesMap.value,
  availableBus: availableBus.value,
  busFunctionalities: busFunctionalities.value,
  busGroupExceptions: busGroupExceptions.value,
  busProductExceptions: busProductExceptions.value,
  language: language.value,
  user: user.value,
  tenant: tenant.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  setSelectedBu: (value: any) => dispatch(setSelectedBu(value)),
  addToGlobalStyles: (value: any) => dispatch(addToGlobalStyles(value)),
  setBusFuncMap: (value: any) => dispatch(setBusFuncMap(value)),
  setBusFuncionalities: (value: any) => dispatch(setBusFuncionalities(value)),
  setWizard: (value: any) => dispatch(setWizard(value)),
  addToWizard: (value: any) => dispatch(addToWizard(value)),
  setStrings: (value: any) => dispatch(setStrings(value)),
  setPatients: (value: any) => dispatch(setPatients(value)),
  setCustomerCardModalTrigger: (value: any) =>
    dispatch(setCustomerCardModalTrigger(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)(React.memo(CustomerCard)),
);
